import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { HelpContext, HelpContextValue, HelpStep } from './HelpContextV2';
import { useLocalStorageState } from 'ahooks';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import { useGetStoresLabel } from './ActivityForm/Stores';
import { notSupportItemsTypes, useGetRecommendItemsLabel } from './ActivityForm/RecommendItems';
import { useGetItemsLabel } from './ActivityForm/Items';
import { useGetTimeRangeLabel } from './ActivityForm/TimeRange';
import { useGetPrepareTimeRangeLabel } from './ActivityForm/PrepareTimeRange';

export function HelpContextProvider({
  children,
  inline = false,
  activityType,
}: {
  children: ReactNode;
  inline?: boolean;
  activityType?: Knowledge.Activity.Type;
}) {
  const { t, getStoresLabel } = useGetStoresLabel();
  const { getRecommendItemsLabel } = useGetRecommendItemsLabel();
  const { getItemsLabel } = useGetItemsLabel();
  const { getTimeRangeLabel } = useGetTimeRangeLabel();
  const { getPrepareTimeRangeLabel } = useGetPrepareTimeRangeLabel();

  const { ACTIVITY_HELP_HIDDED = false } = useTriggerMapValue();
  const createTrigger = useCreateTrigger();

  const displayState = useLocalStorageState('@fett/displayActivityHelp', { defaultValue: true });

  const stepState = useState<HelpStep>('促销类型');

  const getStepTitleMap = useCallback(
    (activityType?: Knowledge.Activity.Type) => {
      return {
        促销类型: t('促销类型'),
        活动名称: t('活动名称'),
        活动生效期: activityType ? getTimeRangeLabel(activityType) : t('活动生效期'),
        活动预热期: activityType ? getPrepareTimeRangeLabel(activityType) : t('活动预热期'),
        适用店铺: activityType ? getStoresLabel(activityType) : t('act_field_store'),
        适用商品: !activityType
          ? t('act_field_items')
          : notSupportItemsTypes.includes(activityType)
          ? getRecommendItemsLabel(activityType)
          : getItemsLabel(activityType),
        最高折扣: t('act_field_discount'),
        活动卖点: t('act_field_maidian'),
        专属销售方案: t('生成专属销售方案'),
      };
    },
    [
      t,
      getStoresLabel,
      getRecommendItemsLabel,
      getItemsLabel,
      getTimeRangeLabel,
      getPrepareTimeRangeLabel,
    ],
  );

  const stepTitleMap = useMemo(
    () => getStepTitleMap(activityType),
    [activityType, getStepTitleMap],
  );

  const stepContentMap = useMemo(() => {
    return {
      促销类型: t('促销类型引导语'),
      活动名称: t('活动名称引导语'),
      活动生效期: t('活动生效期引导语'),
      活动预热期: t('活动预热期引导语'),
      适用店铺: t('适用店铺引导语'),
      适用商品: t('适用商品引导语'),
      最高折扣: t('最高折扣引导语'),
      活动卖点: t('活动卖点引导语'),
      专属销售方案: t('专属销售方案引导语'),
    };
  }, [t]);

  const value = useMemo(() => {
    return {
      displayState,
      hiddedState: [ACTIVITY_HELP_HIDDED, createTrigger],
      stepState,
      stepTitleMap,
      stepContentMap,
      inline,
      getStepTitleMap,
    } as HelpContextValue;
  }, [
    displayState,
    ACTIVITY_HELP_HIDDED,
    createTrigger,
    stepState,
    stepTitleMap,
    stepContentMap,
    inline,
    getStepTitleMap,
  ]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelpContext() {
  const value = useContext(HelpContext);

  if (!value) {
    throw new Error('useHelpContext should be used in a context of HelpContextProvider component');
  }

  return value;
}
